import type {
  FeedMappingView,
  FeedWiring,
  FeedWiringSegment,
  GeneratedStatus,
  ResolvedSection,
  SaveFeedMappingRequest,
} from "@keydials/api-client";

/**
 * The decisions behind the Feeds wiring board (KEY-359): what a click on each dial means, which statuses
 * the two ends draw from, and how a drawn wire becomes a rule the server will accept.
 *
 * Kept apart from the component for the same reason `feedScopePaint` is: this is the part that decides
 * what a rule actually says, and a component test would exercise it only by accident.
 *
 * **Where the two kinds come from.** The dial reports a click on a section band and a click on a wedge
 * separately, and those map onto the server's two status kinds with nothing invented — a wedge is one
 * action point, so it mirrors; a band is a scope, so it aggregates. That is also the doc's own example of
 * the most useful mode: *when every item in their section is finished, set my action point to Complete.*
 */

/** What a click on their dial selected. */
export type WireSource =
  | { kind: "section"; sectionKey: string }
  | { kind: "segment"; sectionKey: string; segmentKey: string };

/** What a click on your dial selected. Always one action point — a section has no status of its own. */
export type WireTarget = { sectionKey: string; segmentKey: string };

/** The rung an aggregate fires on. Deliberately the two an author can state in a sentence. */
export type WireWhen = "allTerminal" | "anyOf";

/** A wire being drawn or edited, before it becomes a request. */
export type WireDraft = {
  name: string;
  source: WireSource;
  /** Itemised source only: the item and axis the rule reads. */
  sourceItemKey: string | null;
  sourceDimensionKey: string | null;
  target: WireTarget;
  /** Itemised target only. */
  targetItemKey: string | null;
  targetDimensionKey: string | null;
  /** Mirror: their status key → yours. Keys with no entry leave your node alone. */
  translation: Record<string, string>;
  /** Aggregate: the single condition, and what to set when it doesn't hold. */
  when: WireWhen;
  whenStatusKeys: string[];
  thenStatusKey: string;
  otherwiseStatusKey: string | null;
  autoApply: boolean;
};

export type WireKind = "Mirror" | "Aggregate";

export const wireKind = (source: WireSource): WireKind => (source.kind === "section" ? "Aggregate" : "Mirror");

/** How the source's palettes are shaped in the two config records the server already understands. */
type StatusMapJson = { entries: { from: string; to: string }[] };
type AggregateJson = {
  excludeStatusKeys: string[];
  conditions: { when: string; statusKeys: string[] | null; percent: number | null; thenTargetStatusKey: string }[];
  fallbackTargetStatusKey: string | null;
};

/** A blank wire for a freshly drawn pair, named after both ends so it reads as a sentence in the list. */
export function newDraft(
  wiring: FeedWiring,
  source: WireSource,
  target: WireTarget,
  targetSections: ResolvedSection[],
): WireDraft {
  const sourceLabel = describeSource(wiring, source);
  const targetLabel = describeTarget(targetSections, target);
  const dimension = wiring.dimensions[0]?.key ?? null;
  const item = source.kind === "segment" ? (segmentOf(wiring, source)?.items[0]?.key ?? null) : null;

  return {
    name: `${sourceLabel} → ${targetLabel}`,
    source,
    // Itemised sources address an item and an axis; the first of each is a starting point, not a guess
    // that survives — the row shows both as selects the moment they matter.
    sourceItemKey: wiring.sourceKind === "Itemised" ? item : null,
    sourceDimensionKey: wiring.sourceKind === "Itemised" ? dimension : null,
    target,
    targetItemKey: null,
    targetDimensionKey: null,
    translation: {},
    when: "allTerminal",
    whenStatusKeys: [],
    thenStatusKey: "",
    otherwiseStatusKey: null,
    autoApply: false,
  };
}

/** The action point a segment-kind source names, if it is still there. */
export function segmentOf(wiring: FeedWiring, source: WireSource): FeedWiringSegment | undefined {
  if (source.kind !== "segment") return undefined;
  return wiring.sections
    .find((s) => s.key === source.sectionKey)
    ?.segments.find((g) => g.key === source.segmentKey);
}

/**
 * The statuses the source end can produce.
 *
 * On an itemised source that is the chosen axis's palette. On a simple one it is the action point's own
 * palette, or — for an aggregate over a whole section — the union across that section, because a section
 * can span action points whose palettes were overridden individually.
 */
export function sourceStatuses(wiring: FeedWiring, draft: WireDraft): GeneratedStatus[] {
  if (wiring.sourceKind === "Itemised") {
    return wiring.dimensions.find((d) => d.key === draft.sourceDimensionKey)?.palette.statuses ?? [];
  }

  if (draft.source.kind === "segment") {
    return segmentOf(wiring, draft.source)?.palette.statuses ?? [];
  }

  const section = wiring.sections.find((s) => s.key === draft.source.sectionKey);
  return dedupeByKey((section?.segments ?? []).flatMap((g) => g.palette.statuses));
}

/** The statuses your end can be set to: your axis's palette when itemised, else the action point's own. */
export function targetStatuses(
  sections: ResolvedSection[],
  defaultStatuses: GeneratedStatus[],
  palettes: { name: string; statuses: GeneratedStatus[] }[],
  dimensions: { key: string; palette: { statuses: GeneratedStatus[] } }[] | undefined,
  draft: WireDraft,
): GeneratedStatus[] {
  if (draft.targetDimensionKey) {
    return dimensions?.find((d) => d.key === draft.targetDimensionKey)?.palette.statuses ?? [];
  }

  const segment = sections
    .find((s) => s.key === draft.target.sectionKey)
    ?.segments.find((g) => g.key === draft.target.segmentKey);
  const named = segment?.palette ? palettes.find((p) => p.name === segment.palette) : undefined;
  return named?.statuses ?? defaultStatuses;
}

/**
 * Turns a drawn wire into the rule the server stores.
 *
 * The config blob is camelCase because that is what a browser naturally writes; the server reads it
 * case-insensitively for exactly that reason. Nothing here decides whether the rule is *allowed* — the
 * server re-derives that from both dials and refuses with named problems, which is the only place that
 * judgement can honestly be made.
 */
export function toSaveRequest(feedId: string, draft: WireDraft): SaveFeedMappingRequest {
  const kind = wireKind(draft.source);
  const aggregate = kind === "Aggregate";

  const config: StatusMapJson | AggregateJson = aggregate
    ? {
        excludeStatusKeys: [],
        conditions: [
          {
            when: draft.when,
            statusKeys: draft.when === "anyOf" ? draft.whenStatusKeys : null,
            percent: null,
            thenTargetStatusKey: draft.thenStatusKey,
          },
        ],
        fallbackTargetStatusKey: draft.otherwiseStatusKey,
      }
    : { entries: Object.entries(draft.translation).filter(([, to]) => to).map(([from, to]) => ({ from, to })) };

  return {
    feedId,
    kind,
    name: draft.name.trim(),
    source: {
      sectionKey: draft.source.sectionKey,
      // An aggregate reads a scope, so a section-kind source names no action point at all.
      segmentKey: draft.source.kind === "segment" ? draft.source.segmentKey : null,
      itemKey: aggregate ? null : draft.sourceItemKey,
      dimensionKey: draft.sourceDimensionKey,
      attributeKey: null,
    },
    target: {
      sectionKey: draft.target.sectionKey,
      segmentKey: draft.target.segmentKey,
      itemKey: draft.targetItemKey,
      dimensionKey: draft.targetDimensionKey,
      attributeKey: null,
    },
    config: JSON.stringify(config),
    autoApply: draft.autoApply,
  };
}

/** Reopens a stored wire for editing. Config that no longer parses reopens blank rather than throwing. */
export function draftFromMapping(mapping: FeedMappingView): WireDraft {
  const parsed = parseConfig(mapping.config);
  const condition = parsed.aggregate?.conditions?.[0];

  return {
    name: mapping.name,
    source:
      mapping.source.segmentKey && mapping.kind === "Mirror"
        ? { kind: "segment", sectionKey: mapping.source.sectionKey, segmentKey: mapping.source.segmentKey }
        : { kind: "section", sectionKey: mapping.source.sectionKey },
    sourceItemKey: mapping.source.itemKey,
    sourceDimensionKey: mapping.source.dimensionKey,
    target: { sectionKey: mapping.target.sectionKey, segmentKey: mapping.target.segmentKey ?? "" },
    targetItemKey: mapping.target.itemKey,
    targetDimensionKey: mapping.target.dimensionKey,
    translation: Object.fromEntries((parsed.map?.entries ?? []).map((e) => [e.from, e.to])),
    when: condition?.when === "anyOf" ? "anyOf" : "allTerminal",
    whenStatusKeys: condition?.statusKeys ?? [],
    thenStatusKey: condition?.thenTargetStatusKey ?? "",
    otherwiseStatusKey: parsed.aggregate?.fallbackTargetStatusKey ?? null,
    autoApply: mapping.autoApply,
  };
}

/** Whatever the stored blob turns out to be, without ever throwing on a shape that has moved on. */
export function parseConfig(config: string | null): { map?: StatusMapJson; aggregate?: AggregateJson } {
  if (!config) return {};
  try {
    const raw = JSON.parse(config) as Partial<StatusMapJson & AggregateJson>;
    if (Array.isArray(raw.entries)) return { map: { entries: raw.entries } };
    if (Array.isArray(raw.conditions)) {
      return {
        aggregate: {
          excludeStatusKeys: raw.excludeStatusKeys ?? [],
          conditions: raw.conditions,
          fallbackTargetStatusKey: raw.fallbackTargetStatusKey ?? null,
        },
      };
    }
    return {};
  } catch {
    return {};
  }
}

/** Everything still missing before the server could possibly accept this wire. */
export function draftProblems(draft: WireDraft, wiring: FeedWiring): string[] {
  const problems: string[] = [];
  if (!draft.name.trim()) problems.push("Give this wire a name.");

  if (wiring.sourceKind === "Itemised" && !draft.sourceDimensionKey) {
    problems.push("Choose which of their axes to read.");
  }
  if (wireKind(draft.source) === "Mirror" && wiring.sourceKind === "Itemised" && !draft.sourceItemKey) {
    problems.push("Choose which of their items to read.");
  }

  if (wireKind(draft.source) === "Aggregate") {
    if (!draft.thenStatusKey) problems.push("Choose the status to set when the condition holds.");
    if (draft.when === "anyOf" && draft.whenStatusKeys.length === 0) {
      problems.push("Choose at least one of their statuses to watch for.");
    }
  } else if (Object.values(draft.translation).every((to) => !to)) {
    problems.push("Translate at least one of their statuses — matching names across two organisations mean nothing on their own.");
  }

  return problems;
}

/** "A · A1", or just "A" for a whole-section aggregate. */
export function describeSource(wiring: FeedWiring, source: WireSource): string {
  const section = wiring.sections.find((s) => s.key === source.sectionKey);
  const sectionLabel = section?.label ?? source.sectionKey;
  if (source.kind === "section") return sectionLabel;
  const segment = section?.segments.find((g) => g.key === source.segmentKey);
  return `${sectionLabel} · ${segment?.label ?? source.segmentKey}`;
}

export function describeTarget(sections: ResolvedSection[], target: WireTarget): string {
  const section = sections.find((s) => s.key === target.sectionKey);
  const segment = section?.segments.find((g) => g.key === target.segmentKey);
  return `${section?.label ?? target.sectionKey} · ${segment?.name ?? target.segmentKey}`;
}

/** Your own sections — never the ones another organisation feeds you. A feed cannot be fed. */
export const ownSections = (sections: ResolvedSection[]): ResolvedSection[] => sections.filter((s) => !s.fedFrom);

/** Every node one of these wires already writes to, as "section/segment", for painting your dial. */
export function wiredTargets(mappings: FeedMappingView[]): Set<string> {
  return new Set(mappings.map((m) => `${m.target.sectionKey}/${m.target.segmentKey ?? ""}`));
}

/** Every node on their dial one of these wires already reads, as "section" or "section/segment". */
export function wiredSources(mappings: FeedMappingView[]): Set<string> {
  return new Set(
    mappings.map((m) => (m.kind === "Aggregate" ? m.source.sectionKey : `${m.source.sectionKey}/${m.source.segmentKey ?? ""}`)),
  );
}

function dedupeByKey(statuses: GeneratedStatus[]): GeneratedStatus[] {
  const seen = new Set<string>();
  return statuses.filter((s) => (seen.has(s.key) ? false : (seen.add(s.key), true)));
}
